import React, { useState, useEffect, useCallback } from 'react';
import { publicAPI } from '../services/api';

const CATEGORIES = ['All', 'Tree Knowledge', 'Heritage', 'CSR Impact', 'Technology', 'Environment', 'Community', 'Green Offerings'];

// Fallback static posts for when API has no data yet
const fallbackPosts = [
  { id: 'f1', title: "Why Neem Trees Are India's Green Guardians", summary: "Discover how the humble Neem tree has been protecting Indian ecosystems for centuries, offering shade, medicine, and natural pest control to communities across the subcontinent.", author_name: "Dr. Priya Sharma", published_at: "2026-02-20", category: "Tree Knowledge", read_time_min: 5, featured_image: "/tree-images/neem.jpg" },
  { id: 'f2', title: "The Sacred Banyan: Preserving Living Heritage", summary: "Banyan trees, with their sprawling aerial roots, are ecological marvels that support entire ecosystems. Learn why preserving these ancient giants is critical for biodiversity.", author_name: "Amit Verma", published_at: "2026-02-15", category: "Heritage", read_time_min: 4, featured_image: "/tree-images/banyan.jpg" },
  { id: 'f3', title: "Corporate Plantation Drives: A CSR Success Story", summary: "How leading Indian corporations are meeting their CSR goals through large-scale tree plantation programs, creating green corridors and offsetting carbon emissions.", author_name: "Haritanjali Team", published_at: "2026-02-10", category: "CSR Impact", read_time_min: 6, featured_image: "/tree-images/teak.jpg" },
  { id: 'f4', title: "GPS Monitoring: Bringing Transparency to Plantation", summary: "How Haritanjali uses GPS-tagged plantation monitoring with quarterly NSEW photos to ensure every sponsored tree is planted, growing, and thriving.", author_name: "Tech Team", published_at: "2026-02-05", category: "Technology", read_time_min: 3, featured_image: "/tree-images/peepal.jpg" },
  { id: 'f5', title: "Mango Trees & Carbon Sequestration", summary: "Beyond delicious fruit, mango trees are powerful carbon sinks. One mature mango tree can absorb up to 48 kg of CO\u2082 per year while providing livelihood to farmers.", author_name: "Dr. Rajan Mehta", published_at: "2026-01-28", category: "Environment", read_time_min: 5, featured_image: "/tree-images/mango.jpg" },
  { id: 'f6', title: "Volunteer Stories: Planting Hope in Rajasthan", summary: "Meet the volunteers who are transforming barren lands in Rajasthan into green oases, one tree at a time. Their stories of dedication and hope inspire a movement.", author_name: "Neha Gupta", published_at: "2026-01-20", category: "Community", read_time_min: 4, featured_image: "/tree-images/amla.jpg" },
  { id: 'f7', title: "Biodiversity Add-ons: Beyond Just Planting Trees", summary: "Explore how adding beehives, bird nests, and micro ponds alongside tree plantations creates complete ecosystems that support wildlife and enhance green cover.", author_name: "Haritanjali Team", published_at: "2026-01-15", category: "Green Offerings", read_time_min: 4, featured_image: "/tree-images/bamboo.jpg" },
  { id: 'f8', title: "How LGD Codes Help Map India's Green Future", summary: "Understanding Local Government Directory codes and how they enable precise plantation location tracking across India's states and districts.", author_name: "Tech Team", published_at: "2026-01-10", category: "Technology", read_time_min: 3, featured_image: "/tree-images/ashoka.jpg" },
];

function formatBlogDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailPost, setDetailPost] = useState(null);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch ALL published posts first, then filter client-side for consistent counts
      const res = await publicAPI.getBlogPosts({ per_page: 100 });
      const allApiPosts = res.data.posts || [];
      if (allApiPosts.length > 0) {
        const filtered = selectedCategory === 'All' ? allApiPosts : allApiPosts.filter(p => (p.category || '').toLowerCase() === selectedCategory.toLowerCase());
        setPosts(filtered);
      } else {
        // Use fallback when API has no data at all
        const filtered = selectedCategory === 'All' ? fallbackPosts : fallbackPosts.filter(p => p.category === selectedCategory);
        setPosts(filtered);
      }
    } catch {
      // Fallback to static data on error
      const filtered = selectedCategory === 'All' ? fallbackPosts : fallbackPosts.filter(p => p.category === selectedCategory);
      setPosts(filtered);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  // Load full post detail when selectedPost is set
  const openPost = async (post) => {
    if (typeof post.id === 'string' && post.id.startsWith('f')) {
      // Fallback post - show as-is
      setDetailPost(post);
      setSelectedPost(post);
      return;
    }
    try {
      const res = await publicAPI.getBlogPost(post.id);
      setDetailPost(res.data.post);
      setSelectedPost(res.data.post);
    } catch {
      setDetailPost(post);
      setSelectedPost(post);
    }
  };

  if (selectedPost) {
    const p = detailPost || selectedPost;
    return (
      <div style={{ minHeight: '100vh', background: '#f8faf5' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 20px' }}>
          <button onClick={() => { setSelectedPost(null); setDetailPost(null); }} style={{ background: 'none', border: 'none', color: '#2e7d32', cursor: 'pointer', fontSize: 15, fontWeight: 600, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
            &larr; Back to Blog
          </button>
          <div style={{ background: '#ddd', height: 300, borderRadius: 16, marginBottom: 24, backgroundImage: p.featured_image ? `url(${p.featured_image})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }} />
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ background: '#2e7d32', color: '#fff', padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600 }}>{p.category}</span>
            <span style={{ color: '#999', fontSize: 13 }}>{p.read_time_min} min read</span>
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: '#1b5e20', marginBottom: 16, lineHeight: 1.3 }}>{p.title}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 30, color: '#666', fontSize: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#2e7d32' }}>
              {(p.author_name || 'A')[0]}
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#333' }}>{p.author_name || 'Haritanjali Team'}</div>
              <div style={{ fontSize: 12 }}>{formatBlogDate(p.published_at)}</div>
            </div>
          </div>
          <div style={{ fontSize: 16, lineHeight: 1.8, color: '#444' }}>
            {p.content ? (
              <div dangerouslySetInnerHTML={{ __html: p.content.replace(/\n/g, '<br/>') }} />
            ) : (
              <>
                <p>{p.summary}</p>
                <p>Tree plantations play a vital role in combating climate change, preventing soil erosion, and supporting biodiversity. Through Haritanjali, every individual and organization can contribute to a greener future.</p>
                <p>Our GPS-monitored plantations ensure complete transparency, with quarterly updates including photographs from all four directions (North, South, East, West). This level of accountability sets a new standard for environmental initiatives in India.</p>
                <p>Whether you're an individual looking to offset your carbon footprint, a corporation fulfilling CSR objectives, or a plantation agency executing on the ground, Haritanjali provides the tools, technology, and transparency needed for meaningful environmental impact.</p>
                <p>Join us in making a difference &mdash; one tree at a time.</p>
              </>
            )}
          </div>
          <div style={{ background: '#e8f5e9', padding: 20, borderRadius: 12, marginTop: 30 }}>
            <div style={{ fontWeight: 600, color: '#2e7d32', marginBottom: 12, fontSize: 15 }}>Share this article</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(p.title + ' - ' + window.location.href)}`}
                target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: '#25D366', color: '#fff', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
                WhatsApp
              </a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: '#1877F2', color: '#fff', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
                Facebook
              </a>
              <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(p.title)}&url=${encodeURIComponent(window.location.href)}`}
                target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: '#000', color: '#fff', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
                X (Twitter)
              </a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: '#0A66C2', color: '#fff', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
                LinkedIn
              </a>
              <button onClick={() => { navigator.clipboard.writeText(window.location.href); alert('Link copied!'); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: '#666', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Copy Link
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8faf5' }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #1b5e20, #2e7d32, #43a047)', padding: '60px 20px', textAlign: 'center', color: '#fff' }}>
        <h1 style={{ fontSize: 36, fontWeight: 800, marginBottom: 10 }}>Haritanjali Blog</h1>
        <p style={{ fontSize: 16, opacity: 0.85, maxWidth: 600, margin: '0 auto' }}>Stories, knowledge & updates from India's green movement</p>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '30px 20px' }}>
        {/* Category Filter */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 30 }}>
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setSelectedCategory(cat)}
              style={{ padding: '8px 18px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: selectedCategory === cat ? 700 : 500,
                background: selectedCategory === cat ? '#2e7d32' : '#fff', color: selectedCategory === cat ? '#fff' : '#333',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)', transition: 'all 0.2s' }}>
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>Loading blog posts...</div>
        ) : posts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>No blog posts found in this category.</div>
        ) : (
          <>
            {/* Featured Post */}
            <div onClick={() => openPost(posts[0])} style={{ cursor: 'pointer', borderRadius: 20, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', marginBottom: 30, display: 'flex', background: '#fff', transition: 'transform 0.2s' }}>
              <div style={{ width: '50%', minHeight: 300, backgroundImage: posts[0].featured_image ? `url(${posts[0].featured_image})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#e8f5e9' }} />
              <div style={{ padding: 30, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center' }}>
                  <span style={{ background: '#2e7d32', color: '#fff', padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{posts[0].category}</span>
                  <span style={{ color: '#999', fontSize: 12 }}>{posts[0].read_time_min} min read</span>
                </div>
                <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 10, color: '#1b5e20' }}>{posts[0].title}</h2>
                <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>{posts[0].summary}</p>
                <div style={{ fontSize: 13, color: '#999' }}>{posts[0].author_name || 'Haritanjali Team'} &middot; {formatBlogDate(posts[0].published_at)}</div>
              </div>
            </div>

            {/* Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
              {posts.slice(1).map(post => (
                <div key={post.id} onClick={() => openPost(post)} style={{ cursor: 'pointer', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', transition: 'transform 0.2s, box-shadow 0.2s' }}>
                  <div style={{ height: 180, backgroundImage: post.featured_image ? `url(${post.featured_image})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#e8f5e9' }} />
                  <div style={{ padding: 18 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                      <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600 }}>{post.category}</span>
                      <span style={{ color: '#999', fontSize: 11, marginLeft: 'auto' }}>{post.read_time_min} min read</span>
                    </div>
                    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: '#333' }}>{post.title}</h3>
                    <p style={{ fontSize: 13, color: '#666', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.summary}</p>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 10 }}>{post.author_name || 'Haritanjali Team'} &middot; {formatBlogDate(post.published_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
